<template>
  <Sidebar collapsible="icon">
    <SidebarHeader>
      <div
        class="tw-flex tw-items-center tw-justify-center"
        :style="state === 'expanded' ? 'height: 64px;' : 'height: 64px;'"
      >
        <router-link :to="{ name: 'dashboard' }">
          <div
            class="logo-container"
            :style="
              state === 'expanded'
                ? 'width: 96px; height: 48px; display: flex; align-items: center; justify-content: center;'
                : 'width: 48px; height: 48px; display: flex; align-items: center; justify-content: center;'
            "
          >
            <InocuLogo v-if="state === 'expanded'" class="tw-w-24" />
            <InocuLogoCollapsed v-else class="tw-w-6 tw-h-6" />
          </div>
        </router-link>
      </div>
      <!-- Tenant Switcher -->
      <SidebarMenu v-if="state === 'expanded'">
        <SidebarMenuItem>
          <TenantSwitcher />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup>
        <SidebarMenu>
          <template v-for="item in mainNavItems" :key="item.title">
            <!-- Collapsible items with sub-items -->
            <!-- @vue-ignore -->
            <Collapsible
              v-if="item.items && item.items.length > 0 && state === 'expanded'"
              v-slot="{ open }"
              :default-open="item.isActive"
              class="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger as-child>
                  <SidebarMenuButton :tooltip="t(item.title)">
                    <component :is="item.icon" v-if="item.icon" />
                    <span>{{ t(item.title) }}</span>
                    <ChevronRight
                      :class="[
                        'tw-ml-auto tw-transition-transform tw-duration-200',
                        open ? 'tw-rotate-90' : 'tw-rotate-0',
                      ]"
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <template v-for="subItem in item.items" :key="subItem.routerName">
                      <SidebarMenuSubItem
                        v-if="
                          subItem.allowedRoles.some(allowedRole =>
                            currentUser?.user_role.includes(allowedRole)
                          )
                        "
                      >
                        <SidebarMenuSubButton
                          v-if="subItem.routerName === 'create-customer'"
                          :as-child="true"
                          :is-active="isActiveRoute(subItem.routerName)"
                          @click="navigateToCustomerCreation()"
                        >
                          <div role="button" tabindex="0">
                            <span>{{ t(subItem.nameKey) }}</span>
                          </div>
                        </SidebarMenuSubButton>
                        <SidebarMenuSubButton
                          v-else
                          :as-child="true"
                          :is-active="isActiveRoute(subItem.routerName)"
                        >
                          <router-link
                            :to="{ name: subItem.routerName }"
                            class="tw-text-gray-400 hover:tw-text-white"
                          >
                            <span>{{ t(subItem.nameKey) }}</span>
                          </router-link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </template>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
            <DropdownMenu v-else-if="item.items && item.items.length > 0 && state === 'collapsed'">
              <DropdownMenuTrigger as-child>
                <SidebarMenuItem>
                  <SidebarMenuButton :tooltip="t(item.title)">
                    <component :is="item.icon" v-if="item.icon" />
                    <span>{{ t(item.title) }}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                class="!tw-border-none group !tw-shadow-[0px_0px_2px_0px_#f5f5f5] !tw-bg-[#24273B] tw-z-[200] tw-min-w-[150px]"
              >
                <DropdownMenuItem
                  v-for="subItem in item.items"
                  :key="subItem.routerName"
                  class="group !tw-bg-[#24273B] tw-cursor-pointer hover:!tw-bg-[#24273B] hover:!tw-text-white"
                >
                  <router-link
                    :to="{ name: subItem.routerName }"
                    class="tw-text-gray-400 hover:!tw-text-white"
                  >
                    <span>{{ t(subItem.nameKey) }}</span>
                  </router-link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <!-- Regular menu items without sub-items -->
            <SidebarMenuItem v-else-if="!item.items || item.items.length === 0">
              <SidebarMenuButton
                :as-child="true"
                :is-active="item.routerName ? isActiveRoute(item.routerName) : false"
                :tooltip="t(item.title)"
              >
                <router-link
                  v-if="item.routerName"
                  :to="{ name: item.routerName }"
                  class="tw-flex tw-items-center tw-gap-2 tw-text-gray-400 hover:tw-text-white"
                >
                  <component :is="item.icon" v-if="item.icon" />
                  <span>{{ t(item.title) }}</span>
                  <SidebarMenuBadge
                    v-if="item.routerName === 'notification-center' && notReadNotificationsCount"
                  >
                    <Badge variant="secondary" class="tw-text-xs">
                      {{ notReadNotificationsCount }}
                    </Badge>
                  </SidebarMenuBadge>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </template>
        </SidebarMenu>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupLabel>{{ t('sidebar.titles.account') }}</SidebarGroupLabel>
        <SidebarMenu>
          <template v-for="item in secondaryNavItems" :key="item.title">
            <SidebarMenuItem
              v-if="
                item.allowedRoles.some(allowedRole =>
                  currentUser?.user_role.includes(allowedRole)
                ) || item.routerName === 'my-profile'
              "
            >
              <SidebarMenuButton
                :as-child="true"
                :is-active="item.routerName ? isActiveRoute(item.routerName) : false"
                :tooltip="t(item.title)"
              >
                <router-link
                  v-if="item.routerName"
                  :to="{ name: item.routerName }"
                  class="tw-flex tw-items-center tw-gap-2 tw-text-gray-400 hover:tw-text-white"
                >
                  <component :is="item.icon" v-if="item.icon" />
                  <span>{{ t(item.title) }}</span>
                  <SidebarMenuBadge
                    v-if="item.routerName === 'notification-center' && notReadNotificationsCount"
                  >
                    <Badge variant="custom" class="tw-text-xs tw-bg-green-200 tw-text-green-600">
                      {{ notReadNotificationsCount }}
                    </Badge>
                  </SidebarMenuBadge>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </template>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter v-if="state === 'expanded'">
      <div
        v-if="
          currentUser?.user_role.includes('Order Clearer') ||
          currentUser?.user_role.includes('Approver') ||
          currentUser?.user_role.includes('Payment Clearer')
        "
        class="tw-text-xs tw-text-gray-500 tw-bg-white tw-p-2 tw-border-l-4 tw-border-primary-500 tw-rounded-md tw-mb-2"
      >
        <div class="customer-name">{{ customer.name }}</div>
        <div class="customer-limit">
          {{ t('orders.titles.customer_limit') }}:
          <span>{{ n(+customer.customer_limit, 'currency') }}</span>
        </div>
        <div class="customer-remaining">
          {{ t('orders.titles.remaining_limit') }}:
          <span :style="currentBalance >= 0 ? 'color: #09cf97;' : 'color: #fa5c7c;'">
            {{ n(+currentBalance, 'currency') }}
          </span>
        </div>
      </div>
      <div class="tw-text-xs tw-text-gray-500 tw-text-center">
        © Inocu {{ new Date().getFullYear() }}
      </div>
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import router from '@/router';
import { useCustomerStore } from '@/stores/customer.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { useOrderStore } from '@/stores/order.store';
import { useUserStore } from '@/stores/user.store';
import { ChevronRight } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InocuLogoCollapsed from '../../assets/images/inocu-logo-icon.svg';
import InocuLogo from '../../assets/images/inocu-logo.svg';
import { SideBarNavMain, SideBarNavSecondary } from './side-bar.config';
import cookieService from '@/_helpers/services/cookie.service';
import TenantSwitcher from './TenantSwitcher.vue';

const { t, n } = useI18n();

// Get sidebar state
const { state } = useSidebar();

const { currentUser } = useUserStore();
const { notReadNotificationsCount } = storeToRefs(useNotificationsStore());
const { getMainDataById } = useCustomerStore();
const { getCustomerCurrentBalance } = useOrderStore();

const customer = ref({
  name: '',
  customer_limit: '',
});

const loading = ref(false);
const currentBalance: any = ref(0);

// Computed properties for filtered navigation items
const mainNavItems = computed(() => {
  return SideBarNavMain.filter(
    item =>
      item.allowedRoles.some(allowedRole => currentUser?.user_role.includes(allowedRole)) ||
      item.routerName === 'my-profile'
  );
});

const secondaryNavItems = computed(() => {
  return SideBarNavSecondary.filter(
    item =>
      item.allowedRoles.some(allowedRole => currentUser?.user_role.includes(allowedRole)) ||
      item.routerName === 'my-profile'
  );
});

const isActiveRoute = (routeName: string) => {
  return router.currentRoute.value.name === routeName;
};

const navigateToCustomerCreation = () => {
  router.push({ name: 'create-customer' });
};

onMounted(() => {
  let customerId = cookieService.get('selectedCustomerId');

  if (customerId !== null) {
    getMainDataById(customerId)
      .then(response => {
        customer.value = response;
        loading.value = true;
      })
      .catch(error => console.log(error));

    if (
      currentUser?.user_role.includes('Order Clearer') ||
      currentUser?.user_role.includes('Approver') ||
      currentUser?.user_role.includes('Payment Clearer')
    ) {
      getCustomerCurrentBalance(customerId, null).then(response => {
        currentBalance.value = response.current_balance;
      });
    }
  }
});
</script>

<style scoped>
[data-sidebar='content'] {
  scrollbar-width: thin;
  scrollbar-color: #b1b1b1 #24273b;
}
[data-sidebar='content']::-webkit-scrollbar {
  width: 4px;
  background: #24273b;
}
[data-sidebar='content']::-webkit-scrollbar-thumb {
  background: #35395a;
  border-radius: 4px;
}
[data-sidebar='content']::-webkit-scrollbar-thumb:hover {
  background: #50547a;
}
[data-sidebar='content']::-webkit-scrollbar-corner {
  background: #24273b;
}
</style>
